<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="600px"
    persistent
  >
    <v-card>
      <v-card-title class="headline primary white--text">
        <v-icon left dark>{{
          isEditing ? "mdi-pencil" : "mdi-plus-circle"
        }}</v-icon>
        {{
          isEditing
            ? isTitleAccount
              ? "Muokkaa otsikkoriviä"
              : "Muokkaa budjettitiliä"
            : "Lisää uusi budjettitili"
        }}
      </v-card-title>

      <v-card-text class="pt-5">
        <v-alert v-if="isTitleAccount" type="info" dense text class="mb-3">
          Tämä on otsikkorivi. Otsikon summat (arvio ja toteutuma) lasketaan
          automaattisesti sen alariveistä.
        </v-alert>

        <v-form ref="form" v-model="valid">
          <v-row dense>
            <!-- Valinta: Tavallinen tili vai Otsikkorivi (vain uutta luotaessa) -->
            <v-col cols="12" v-if="!isEditing">
              <div class="subtitle-2 grey--text text--darken-2 mb-1">
                Rivityyppi
              </div>
              <v-btn-toggle
                v-model="form.isTitle"
                mandatory
                dense
                color="primary"
                class="mb-2"
                @change="onRowTypeChanged"
              >
                <v-btn :value="false" class="text-none">
                  <v-icon left small>mdi-file-document-outline</v-icon>
                  Tavallinen tili
                </v-btn>
                <v-btn :value="true" class="text-none">
                  <v-icon left small>mdi-format-header-pound</v-icon>
                  Otsikkorivi
                </v-btn>
              </v-btn-toggle>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="form.projectId"
                :items="projectItems"
                item-text="fullName"
                item-value="id"
                label="Kustannuspaikka"
                outlined
                dense
                :disabled="isEditing"
                :rules="[rules.required]"
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.accountNumber"
                :label="
                  isTitleAccount
                    ? 'Otsikkokoodi / Tilinumero (esim. 400, 771, XX1)'
                    : 'Procountor-tili (esim. 4001)'
                "
                outlined
                dense
                :hint="
                  isTitleAccount
                    ? 'Otsikkokoodi tai ryhmänumero'
                    : 'Vähintään 4 numeroa (3-numeroiset ovat otsikkorivejä)'
                "
                persistent-hint
                :rules="[rules.accountNumber]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.subAccount"
                label="Kirjanpidon tili / Sisäinen tili (esim. 3073)"
                outlined
                dense
                hint="VPK:n sisäinen kirjanpidon tilikoodi (valinnainen)"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="form.accountName"
                :label="
                  isTitleAccount ? 'Otsikon nimi *' : 'Tilin nimi / Selite *'
                "
                outlined
                dense
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.category"
                :items="categoryOptions"
                label="Kategoria / Pääryhmä *"
                outlined
                dense
                :rules="[rules.required]"
              ></v-combobox>
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.type"
                :items="typeOptions"
                label="Tilityyppi"
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- Sijainti listalla -->
            <v-col cols="12" sm="6">
              <v-select
                v-model="placementMode"
                :items="currentPlacementModeOptions"
                label="Sijainti listalla"
                outlined
                dense
                hint="Mihin kohtaan rivi sijoitetaan"
                persistent-hint
              ></v-select>
            </v-col>

            <v-col cols="12" sm="6" v-if="placementMode === 'before'">
              <v-autocomplete
                v-model="insertBeforeAccountId"
                :items="availablePlacementAccounts"
                item-text="displayName"
                item-value="id"
                label="Sijoita ennen tätä riviä *"
                outlined
                dense
                clearable
                :rules="[rules.required]"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" v-if="placementMode === 'after'">
              <v-autocomplete
                v-model="insertAfterAccountId"
                :items="availablePlacementAccounts"
                item-text="displayName"
                item-value="id"
                label="Sijoita tämän rivin jälkeen *"
                outlined
                dense
                clearable
                :rules="[rules.required]"
              ></v-autocomplete>
            </v-col>

            <!-- Arvio & Toteutuma: Tavalliselle tilille muokattavissa, otsikkoriville laskettu & readonly -->
            <v-col cols="12" sm="6" v-if="!isTitleAccount">
              <v-text-field
                v-model.number="form.arvio"
                label="Arvio / Budjetoitu summa (€)"
                outlined
                dense
                type="number"
                step="any"
                hint="Kulut syötetään yleensä miinusmerkkisenä (esim. -1000)"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" v-if="!isTitleAccount">
              <v-text-field
                v-model.number="form.baseToteutuma"
                label="Pohjatoteutuma (€)"
                outlined
                dense
                type="number"
                step="any"
                hint="Aiempi toteutuma (ennen CSV-tuonteja)"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" v-if="isTitleAccount">
              <v-text-field
                :value="form.arvio"
                label="Laskettu arvio (€)"
                outlined
                dense
                readonly
                hint="Summa lasketaan automaattisesti alarivien arvioista"
                persistent-hint
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" v-if="isTitleAccount">
              <v-text-field
                :value="form.baseToteutuma"
                label="Laskettu toteutuma (€)"
                outlined
                dense
                readonly
                hint="Summa lasketaan automaattisesti alarivien toteumista"
                persistent-hint
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-btn
          v-if="isEditing"
          color="error"
          text
          @click="confirmDelete"
          :loading="saving"
        >
          <v-icon left>mdi-delete</v-icon>
          {{ isTitleAccount ? "Poista otsikkorivi" : "Poista tili" }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)" :disabled="saving">
          Peruuta
        </v-btn>
        <v-btn
          color="primary"
          @click="save"
          :loading="saving"
          :disabled="!valid"
        >
          Tallenna
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { DEFAULT_CATEGORIES } from "@/utils/budgetExcelParser";

export default {
  name: "BudgetAccountEditDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    account: {
      type: Object,
      default: null,
    },
    projects: {
      type: Array,
      default: () => [],
    },
    defaultProjectId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      valid: true,
      saving: false,
      categoryOptions: DEFAULT_CATEGORIES,
      typeOptions: [
        { text: "Kulu (Expense)", value: "expense" },
        { text: "Tuotto (Income)", value: "income" },
        { text: "Investointi (Investment)", value: "investment" },
        { text: "Tase / Oma pääoma (Equity)", value: "equity" },
      ],
      form: {
        id: "",
        projectId: "",
        accountNumber: "",
        subAccount: "",
        accountName: "",
        category: "Materiaalit ja palvelut",
        type: "expense",
        arvio: 0,
        baseToteutuma: 0,
      },
      placementMode: "auto",
      placementModeOptions: [
        { text: "Automaattinen (tilinumeron mukaan)", value: "auto" },
        { text: "Sijoita ennen valittua riviä", value: "before" },
        { text: "Sijoita valitun rivin jälkeen", value: "after" },
        { text: "Listan loppuun", value: "end" },
      ],
      insertBeforeAccountId: null,
      insertAfterAccountId: null,
      rules: {
        required: (v) => !!v || "Kenttä on pakollinen",
        accountNumber: (v) => {
          if (!v) return "Tilinumero / Otsikkokoodi on pakollinen";
          if (this.isTitleAccount) return true;
          if (/^\d{1,3}$/.test(String(v).trim())) {
            return "Tavallisen tilinumeron tulee olla vähintään 4 numeroa (1-3-numeroiset ovat otsikkorivejä)";
          }
          return true;
        },
      },
    };
  },
  computed: {
    isEditing() {
      return !!(this.account && this.account.id);
    },
    isTitleAccount() {
      return !!(
        (this.account &&
          this.account.id &&
          (this.account.isTitle || this.account.isHeader)) ||
        (this.form && this.form.isTitle)
      );
    },
    projectItems() {
      return this.projects.map((p) => ({
        id: p.id || p.code,
        fullName: p.fullName || `${p.code} ${p.name}`,
      }));
    },
    currentPlacementModeOptions() {
      if (this.isEditing) {
        return [
          { text: "Pidä nykyinen sijainti", value: "keep" },
          { text: "Siirrä ennen valittua riviä", value: "before" },
          { text: "Siirrä valitun rivin jälkeen", value: "after" },
          { text: "Siirrä listan alkuun", value: "start" },
          { text: "Siirrä listan loppuun", value: "end" },
        ];
      }
      return [
        { text: "Automaattinen (tilinumeron mukaan)", value: "auto" },
        { text: "Sijoita ennen valittua riviä", value: "before" },
        { text: "Sijoita valitun rivin jälkeen", value: "after" },
        { text: "Listan alkuun", value: "start" },
        { text: "Listan loppuun", value: "end" },
      ];
    },
    availablePlacementAccounts() {
      const proj = this.projects.find(
        (p) => String(p.id || p.code) === String(this.form.projectId),
      );
      if (!proj || !proj.accounts) return [];
      return proj.accounts
        .filter((a) => !this.form.id || a.id !== this.form.id)
        .map((a) => {
          const prefix = a.accountNumber ? `${a.accountNumber} ` : "";
          const tag = a.isTitle || a.isHeader ? " [OTSIKKO]" : "";
          return {
            id: a.id,
            displayName: `${prefix}${a.accountName || ""}${tag}`,
          };
        });
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.initForm();
      }
    },
  },
  methods: {
    onRowTypeChanged(val) {
      if (val) {
        this.form.isTitle = true;
        this.form.isHeader = true;
        this.form.isModifiable = false;
        this.form.arvio = 0;
        this.form.baseToteutuma = 0;
      } else {
        this.form.isTitle = false;
        this.form.isHeader = false;
        this.form.isModifiable = true;
      }
      if (this.$refs.form) {
        this.$refs.form.validate();
      }
    },
    initForm() {
      this.insertBeforeAccountId = null;
      this.insertAfterAccountId = null;
      if (this.account && this.account.id) {
        this.placementMode = "keep";
        const isTitle = !!(this.account.isTitle || this.account.isHeader);
        this.form = {
          id: this.account.id,
          projectId: this.account.projectId || this.defaultProjectId,
          accountNumber: this.account.accountNumber || "",
          subAccount: this.account.subAccount || "",
          accountName: this.account.accountName || "",
          category: this.account.category || "Materiaalit ja palvelut",
          type: this.account.type || "expense",
          isTitle,
          isHeader: isTitle,
          isModifiable: !isTitle,
          arvio:
            typeof this.account.arvio === "number" ? this.account.arvio : 0,
          baseToteutuma:
            typeof this.account.totalToteutuma === "number"
              ? this.account.totalToteutuma
              : typeof this.account.baseToteutuma === "number"
              ? this.account.baseToteutuma
              : 0,
        };
      } else {
        this.placementMode = "auto";
        const isTitle = !!(
          this.account &&
          (this.account.isTitle || this.account.isHeader)
        );
        this.form = {
          id: "",
          projectId:
            (this.account && this.account.projectId) ||
            this.defaultProjectId ||
            (this.projects[0] ? this.projects[0].id : "10"),
          accountNumber: (this.account && this.account.accountNumber) || "",
          subAccount: (this.account && this.account.subAccount) || "",
          accountName: (this.account && this.account.accountName) || "",
          category:
            (this.account && this.account.category) ||
            "Materiaalit ja palvelut",
          type: (this.account && this.account.type) || "expense",
          isTitle,
          isHeader: isTitle,
          isModifiable: !isTitle,
          arvio: 0,
          baseToteutuma: 0,
        };
      }
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          isTitle: this.isTitleAccount,
          isHeader: this.isTitleAccount,
          isModifiable: !this.isTitleAccount,
          placementMode: this.placementMode,
          insertBeforeAccountId: this.insertBeforeAccountId,
          insertAfterAccountId: this.insertAfterAccountId,
        };
        if (!this.isEditing) {
          if (this.placementMode === "end") {
            payload.insertToEnd = true;
          }
        }
        this.$emit("save", payload);
        this.$emit("input", false);
      } catch (err) {
        this.$toast?.error?.(err.message || "Tallennus epäonnistui");
      } finally {
        this.saving = false;
      }
    },
    confirmDelete() {
      const typeLabel = this.isTitleAccount ? "otsikkorivi" : "tili";
      if (
        confirm(
          `Haluatko varmasti poistaa ${typeLabel}n "${this.form.accountName}"?`,
        )
      ) {
        this.$emit("delete", {
          projectId: this.form.projectId,
          accountId: this.form.id,
          isTitle: this.isTitleAccount,
        });
        this.$emit("input", false);
      }
    },
  },
};
</script>
